<template>
    <div class="flex w-full flex-col gap-6">
        <div class="flex w-full gap-2">
            <diagonal-cut-button
                v-for="(gameType, index) in gameTypes"
                :key="gameType.value"
                class="flex-1 !normal-case [&>*]:py-2 [&>*]:text-sm"
                :cut-position="
                    index === 0 ? 'right-top' : index === gameTypes.length - 1 ? 'left-top' : 'both'
                "
                :variant="selectedGameType == gameType.value ? 'primary' : 'secondary'"
                @click="selectedGameType = gameType.value"
                >{{ $t(gameType.labelKey) }}</diagonal-cut-button
            >
        </div>

        <Panel size="full">
            <div class="flex flex-col gap-6">
                <h2 class="flex text-sm">
                    {{ $t('player_stats.games_played_label') }} <b class="ml-1">{{ periodStats.total_games }}</b>
                    <span class="ml-auto">{{ periodLabel }}</span>
                </h2>
                <BarGraph
                    v-if="hasGamesData && graphData.length > 0"
                    :data="graphData"
                    :labels="graphLabels"
                    :autorange="true"
                    :show-average="true"
                    :min="0"
                ></BarGraph>
                <div v-else class="flex items-center justify-center">
                    <div class="px-6 py-8 text-center">
                        <div class="mb-3 text-4xl">🎯</div>
                        <div class="mb-2 text-lg font-bold text-white">{{ $t('profile.no_games_played') }}</div>
                        <div class="text-sm text-zinc-400">{{ $t('profile.no_games_played_hint') }}</div>
                    </div>
                </div>
            </div>
        </Panel>

        <div class="grid grid-cols-2 gap-4">
            <Panel size="full" class="text-center">
                <div class="mb-4 text-base font-extrabold uppercase">🏆 {{ $t('profile.wins') }}</div>
                <div class="text-3xl font-extrabold">{{ wins }}</div>
            </Panel>

            <Panel size="full" class="text-center">
                <div class="mb-4 text-base font-extrabold uppercase">🎯 {{ $t('profile.avg_throw') }}</div>
                <div class="text-3xl font-extrabold">{{ average }}</div>
            </Panel>
        </div>

        <Panel size="full">
            <div class="flex flex-col gap-6">
                <h2 class="flex">{{ avgThrowLabel }}</h2>
                <BarGraph
                    v-if="hasGamesData"
                    :data="avgThrowData"
                    :labels="graphLabels"
                    :autorange="true"
                    :show-average="true"
                    :min="0"
                ></BarGraph>
                <div v-else class="flex items-center justify-center">
                    <div class="px-6 py-8 text-center">
                        <div class="mb-3 text-4xl">📊</div>
                        <div class="mb-2 text-lg font-bold text-white">{{ $t('profile.no_stats') }}</div>
                        <div class="text-sm text-zinc-400">
                            {{ $t('profile.no_stats_hint') }}
                        </div>
                    </div>
                </div>

                <div class="flex flex-col gap-[1px] bg-zinc-700 p-[1.5px]">
                    <div class="flex bg-zinc-800 p-3 px-4">
                        {{ $t('profile.avg_darts_per_leg') }}
                        <b class="ml-auto">{{
                            periodAggregatedStats.avg_darts_per_leg?.[selectedGameType] || 0
                        }}</b>
                    </div>
                    <div class="flex bg-zinc-900 p-3 px-4">
                        {{ firstNineLabel }} <b class="ml-auto">{{ firstNineValue }}</b>
                    </div>
                </div>
            </div>
        </Panel>

        <div class="flex items-center justify-between w-full">
            <h2 class="font-title text-sm font-bold uppercase flex-1 overflow-hidden overflow-ellipsis">{{ $t('profile.setup_stats') }}</h2>
            <div class="w-40">
                <select-drawer v-model="selectedStats" :presets="statsPresets"
                    >{{ $t('profile.filter_game_stats') }}</select-drawer
                >
            </div>
        </div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SetupCard
                v-for="(setup, index) in userSetups"
                :key="setup.id"
                :setup="setup"
                :selected="index == 0"
                :setup-stats="setupStats"
                :selected-game-type="selectedGameType"
                @selected="selectSetup(setup)"
            />
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'
import { formatDate } from '@/utils/dateHelpers.js'
import SelectDrawer from '@/components/ui/SelectDrawer.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import SetupCard from '@/components/ui/SetupCard.vue'
import Panel from '@/components/layout/Panel.vue'
import BarGraph from '@/components/ui/graphs/BarGraph.vue'

export default {
    name: 'PlayerStats',
    components: {
        SelectDrawer,
        DiagonalCutButton,
        SetupCard,
        Panel,
        BarGraph,
    },
    props: {
        statistics: {
            type: Object,
            required: false,
            default: null,
        },
        period: {
            type: String,
            default: 'week',
        },
    },
    computed: {
        ...mapStores(useAuthStore, useDartSetupStore),

        allTimeStats() {
            return (
                this.authStore.statistics?.all_time || {
                    wins: {},
                    three_dart_avg: {},
                    avg_darts_per_leg: {},
                    avg_first_nine_darts: {},
                }
            )
        },

        periodAggregatedStats() {
            if (this.statistics?.aggregated_stats) {
                return this.statistics.aggregated_stats
            }
            if (!this.statistics) {
                return {
                    wins: {},
                    three_dart_avg: {},
                    avg_darts_per_leg: {},
                    avg_first_nine_darts: {},
                }
            }
            return this.allTimeStats
        },

        wins() {
            return this.periodAggregatedStats.wins?.[this.selectedGameType] || 0
        },

        average() {
            if (this.statistics?.aggregated_stats?.three_dart_avg) {
                const avg =
                    this.statistics.aggregated_stats.three_dart_avg[this.selectedGameType]
                        ?.average || 0
                return Math.floor(avg * 100) / 100
            }
            const avg = this.allTimeStats.three_dart_avg?.[this.selectedGameType]?.average || 0
            return Math.floor(avg * 100) / 100
        },

        isCricketGame() {
            return this.selectedGameType === 'matchtype_tactics'
        },

        firstNineLabel() {
            return this.isCricketGame ? this.$t('player_stats.first_9_marks') : this.$t('player_stats.avg_first_9')
        },

        firstNineValue() {
            const value =
                this.periodAggregatedStats.avg_first_nine_darts?.[this.selectedGameType] || 0
            if (this.isCricketGame) {
                return Math.floor(value)
            }

            return value
        },

        periodStats() {
            if (!this.statistics) {
                return {
                    period_start: '',
                    period_end: '',
                    total_games: 0,
                }
            }

            return {
                period_start: this.statistics.period_start || '',
                period_end: this.statistics.period_end || '',
                total_games: this.statistics.total_games?.[this.selectedGameType] || 0,
            }
        },

        periodLabel() {
            if (this.period === 'week') {
                return this.$t('player_stats.week_of', { date: this.formatDate(this.periodStats.period_start) })
            } else if (this.period === 'month') {
                const date = new Date(this.periodStats.period_start)
                return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
            } else {
                const start = this.formatDate(this.periodStats.period_start, {
                    month: 'short',
                    year: 'numeric',
                })
                const end = this.formatDate(this.periodStats.period_end, {
                    month: 'short',
                    year: 'numeric',
                })
                return `${start} - ${end}`
            }
        },

        avgThrowLabel() {
            if (this.period === 'week') {
                return this.$t('player_stats.avg_throws_week')
            } else if (this.period === 'month') {
                return this.$t('player_stats.avg_throws_month')
            } else {
                return this.$t('player_stats.avg_throws_all_time')
            }
        },

        graphLabels() {
            return [
                this.$t('player_stats.day_mon'),
                this.$t('player_stats.day_tue'),
                this.$t('player_stats.day_wed'),
                this.$t('player_stats.day_thu'),
                this.$t('player_stats.day_fri'),
                this.$t('player_stats.day_sat'),
                this.$t('player_stats.day_sun'),
            ]
        },

        graphData() {
            if (!this.statistics) {
                return [0, 0, 0, 0, 0, 0, 0]
            }
            const gamesPerDay = this.statistics.games_per_day?.[this.selectedGameType] || {}
            const dayOrder = [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
            ]
            return dayOrder.map((day) => gamesPerDay[day] || 0)
        },

        statsPresets() {
            return [
                { label: this.$t('player_stats.stat_games_played'), value: 'games_played' },
                { label: this.$t('player_stats.stat_wins'), value: 'wins' },
                { label: this.$t('player_stats.stat_avg_throw'), value: 'average' },
                { label: this.$t('player_stats.stat_avg_first_9'), value: 'avg_first_nine' },
                { label: this.$t('player_stats.stat_winrate'), value: 'winrate' },
                { label: this.$t('player_stats.stat_t20_pct'), value: 't20_percentage' },
            ]
        },
        avgThrowData() {
            if (!this.statistics) {
                return [0, 0, 0, 0, 0, 0, 0]
            }
            const avgPerDay = this.statistics.avg_throw_per_day?.[this.selectedGameType] || {}
            const dayOrder = [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
            ]
            return dayOrder.map((day) => Math.floor((avgPerDay[day] || 0) * 100) / 100)
        },

        hasGamesData() {
            return this.periodStats.total_games > 0
        },

        userSetups() {
            const setups = this.authStore.dart_setups || []
            return [...setups].sort((a, b) => {
                const statsA =
                    this.setupStats[a.id]?.[this.selectedGameType]?.[this.selectedStats] || 0
                const statsB =
                    this.setupStats[b.id]?.[this.selectedGameType]?.[this.selectedStats] || 0
                return statsB - statsA // Sort descending
            })
        },

        setupStats() {
            return this.authStore.statistics?.setups || {}
        },
    },
    methods: {
        selectSetup(setup) {
            console.log('Selected setup:', setup)

            this.dartSetupStore.setEntryPoint({ type: 'route', name: this.$route.name })
            this.$router.replace({ name: 'setup-detail', params: { id: setup.id } })
        },
        formatDate,
    },
    data() {
        return {
            selectedTimeframe: 2,
            timeframePresets: [
                { label: 'Day', value: 1 },
                { label: 'Week', value: 7 },
                { label: 'Month', value: 31 },
                { label: 'Year', value: 365 },
            ],
            selectedStats: 'games_played',
            gameTypes: [
                { labelKey: 'player_stats.game_type_x01', value: 'matchtype_501' },
                { labelKey: 'player_stats.game_type_tactics', value: 'matchtype_tactics' },
            ],
            selectedGameType: 'matchtype_501',
        }
    },
}

</script>
